import { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { collection, doc, getDoc, onSnapshot, query, updateDoc, where } from "firebase/firestore";
import { db } from "../../../firebase/firestore";
import "../home.css";
import { getStorageJSON } from "../../../utils/helpers";

export default function Navbar() {
    const navigate = useNavigate();
    const location = useLocation();
    const [showMenu, setShowMenu] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const [cartCount, setCartCount] = useState(0);
    const [showNotifications, setShowNotifications] = useState(false);
    const [notifications, setNotifications] = useState([]);
    const [supportUnreadCount, setSupportUnreadCount] = useState(0);
    const [searchQuery, setSearchQuery] = useState("");
    const [displayName, setDisplayName] = useState("");

    const menuRef = useRef();
    const notificationRef = useRef();

    // ✅ SAFE USER
    const user = getStorageJSON("user", null);

    // 🔥 FETCH USER NAME FROM FIREBASE
    useEffect(() => {
        const fetchUserName = async () => {
            if (!user?.uid) {
                setDisplayName(user?.name || "User");
                return;
            }

            try {
                const userDoc = await getDoc(doc(db, "users", user.uid));
                if (userDoc.exists()) {
                    const userData = userDoc.data();
                    const fetchedName = userData.name || user?.name || "User";
                    setDisplayName(fetchedName);
                } else {
                    setDisplayName(user?.name || "User");
                }
            } catch (error) {
                setDisplayName(user?.name || "User");
            }
        };

        fetchUserName();
    }, [user?.uid, user?.name]);

    // 🔥 GET CART COUNT
    useEffect(() => {
        const updateCount = () => {
            const cart = getStorageJSON("cart", []);
            setCartCount(Array.isArray(cart) ? cart.length : 0);
        };

        updateCount();
        window.addEventListener("storage", updateCount);
        window.addEventListener("cart-updated", updateCount);

        return () => {
            window.removeEventListener("storage", updateCount);
            window.removeEventListener("cart-updated", updateCount);
        };
    }, []);

    // 🔥 CLOSE DROPDOWN OUTSIDE CLICK
    useEffect(() => {
        const handleClick = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) {
                setShowMenu(false);
            }
            if (notificationRef.current && !notificationRef.current.contains(e.target)) {
                setShowNotifications(false);
            }
        };
        document.addEventListener("mousedown", handleClick);
        return () => document.removeEventListener("mousedown", handleClick);
    }, []);

    useEffect(() => {
        if (!user?.uid) {
            const loadNotifications = () => {
                const stored = getStorageJSON("customerNotifications", []);
                setNotifications(Array.isArray(stored) ? stored.slice(0, 4) : []);
            };

            loadNotifications();
            window.addEventListener("storage", loadNotifications);
            window.addEventListener("notifications-updated", loadNotifications);

            return () => {
                window.removeEventListener("storage", loadNotifications);
                window.removeEventListener("notifications-updated", loadNotifications);
            };
        }

        const q = query(collection(db, "notifications"), where("userId", "==", user.uid));
        const unsubscribe = onSnapshot(
            q,
            (snap) => {
                const all = snap.docs
                    .map((d) => ({ firestoreId: d.id, ...d.data() }))
                    .sort((a, b) => Number(b.createdAtMs || 0) - Number(a.createdAtMs || 0));
                setNotifications(all.slice(0, 4));
            },
            () => {
                const fallback = getStorageJSON("customerNotifications", []);
                setNotifications(Array.isArray(fallback) ? fallback.slice(0, 4) : []);
            }
        );

        return () => unsubscribe();
    }, [user?.uid]);

    useEffect(() => {
        if (!user?.uid) {
            setSupportUnreadCount(0);
            return;
        }

        const readKey = `supportChatRead_${user.uid}`;

        const recalcFromMessages = (list) => {
            const lastReadAtMs = Number(localStorage.getItem(readKey) || 0);
            const unread = (Array.isArray(list) ? list : []).filter((item) => {
                const fromAdmin = Boolean(item?.fromAdmin);
                const createdAtMs = Number(item?.createdAtMs || 0);
                return fromAdmin && createdAtMs > lastReadAtMs;
            }).length;
            setSupportUnreadCount(unread);
        };

        const chatQuery = query(collection(db, "support"), where("userId", "==", user.uid));

        const unsubscribe = onSnapshot(
            chatQuery,
            (snap) => {
                const chatRows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
                recalcFromMessages(chatRows);
            },
            () => {
                setSupportUnreadCount(0);
            }
        );

        const onSupportRead = () => {
            setSupportUnreadCount(0);
        };

        window.addEventListener("support-chat-read", onSupportRead);

        return () => {
            unsubscribe();
            window.removeEventListener("support-chat-read", onSupportRead);
        };
    }, [user?.uid]);

    useEffect(() => {
        if (window.innerWidth <= 768) {
            document.body.style.overflow = mobileOpen ? "hidden" : "";
        }

        return () => {
            document.body.style.overflow = "";
        };
    }, [mobileOpen]);

    const handleNav = (path) => {
        if (path === "/support") {
            const supportUserId = user?.uid || "";
            if (supportUserId) {
                localStorage.setItem(`supportChatRead_${supportUserId}`, String(Date.now()));
                window.dispatchEvent(new Event("support-chat-read"));
            }
        }
        navigate(path);
        setMobileOpen(false);
        setShowMenu(false);
        setShowNotifications(false);
    };

    const handleSearch = (e) => {
        if (e.key === "Enter" && searchQuery.trim()) {
            // Emit search event to Home component
            window.dispatchEvent(new CustomEvent("search-items", { detail: searchQuery.toLowerCase() }));
            setSearchQuery("");
        }
    };

    const isActive = (path) => location.pathname === path;
    const unreadCount = notifications.filter((item) => !item.read).length;

    const markAllRead = async () => {
        if (!user?.uid) {
            const allRead = notifications.map((item) => ({ ...item, read: true }));
            localStorage.setItem("customerNotifications", JSON.stringify(allRead));
            setNotifications(allRead);
            window.dispatchEvent(new Event("notifications-updated"));
            return;
        }

        await Promise.all(
            notifications
                .filter((item) => item.firestoreId && !item.read)
                .map((item) => updateDoc(doc(db, "notifications", item.firestoreId), { read: true }))
        ).catch(() => {
            // Keep menu interactive even if backend update fails.
        });
    };

    return (
        <div className="glass-navbar">

            {/* LEFT */}
            <h2 className="logo" onClick={() => handleNav("/home")} style={{ cursor: "pointer" }}>
                Shreeji Surti Swad . 
            </h2>

            {/* SEARCH */}
            <div className="nav-middle">
                <input
                    className="glass-search"
                    placeholder="Search Khaman, Dhokla..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={handleSearch}
                />
            </div>

            {/* 🍔 HAMBURGER */}
            <button
                className="hamburger"
                aria-label="Open navigation menu"
                onClick={() => setMobileOpen(!mobileOpen)}
            >
                {mobileOpen ? "✕" : "☰"}
            </button>

            <button
                className="mobile-cart-shortcut"
                aria-label="Open cart"
                onClick={() => handleNav("/cart")}
            >
                Cart
                {cartCount > 0 && <span className="cart-badge">{cartCount}</span>}
            </button>

            {mobileOpen && (
                <div
                    className="mobile-overlay"
                    onClick={() => {
                        setMobileOpen(false);
                        setShowMenu(false);
                        setShowNotifications(false);
                    }}
                ></div>
            )}

            {/* RIGHT */}
            <div className={`nav-right ${mobileOpen ? "open" : ""}`}>

                <div className="nav-mobile-head">
                    <p>Menu</p>
                    <button
                        type="button"
                        className="nav-mobile-close"
                        onClick={() => {
                            setMobileOpen(false);
                            setShowMenu(false);
                            setShowNotifications(false);
                        }}
                    >
                        Close
                    </button>
                </div>

                {/* NAV LINKS */}
                <button className={`nav-link ${isActive("/home") ? "active" : ""}`} onClick={() => handleNav("/home")}>Home</button>

                <button
                    className={`nav-link ${isActive("/offers") ? "active" : ""}`}
                    onClick={() => handleNav("/offers")}
                >
                    Offers
                </button>

                <button
                    className={`nav-link ${isActive("/orders") ? "active" : ""}`}
                    onClick={() => handleNav("/orders")}
                >
                    Orders
                </button>

                <button
                    className={`nav-link ${isActive("/wishlist") ? "active" : ""}`}
                    onClick={() => handleNav("/wishlist")}
                >
                    Wishlist
                </button>

                <button
                    className={`nav-link ${isActive("/support") ? "active" : ""}`}
                    onClick={() => handleNav("/support")}
                >
                    Help
                    {supportUnreadCount > 0 && <span className="support-unread-badge">{supportUnreadCount}</span>}
                </button>

                <div className="notification-wrap" ref={notificationRef}>
                    <button
                        className={`nav-link notification-btn ${showNotifications ? "active" : ""}`}
                        onClick={() => setShowNotifications((prev) => !prev)}
                        title="Notifications"
                    >
                        {mobileOpen ? `Notifications${unreadCount > 0 ? ` (${unreadCount})` : ""}` : "🔔"}
                        {!mobileOpen && unreadCount > 0 && <span className="mini-badge">{unreadCount}</span>}
                    </button>

                    {showNotifications && (
                        <div className="notification-popover">
                            <div className="popover-head">
                                <b>Notifications</b>
                                <button className="popover-link" onClick={markAllRead}>Mark all read</button>
                            </div>

                            {!notifications.length ? (
                                <p className="popover-empty">No new notifications</p>
                            ) : (
                                notifications.map((item) => (
                                    <article key={item.id} className="popover-item">
                                        <h4>{item.title}</h4>
                                        <p>{item.message}</p>
                                    </article>
                                ))
                            )}

                            <button className="popover-view" onClick={() => handleNav("/notifications")}>
                                View all
                            </button>
                        </div>
                    )}
                </div>

                {/* 🛒 CART */}
                <button id="cart-icon"
                    className="cart-box"
                    onClick={() => handleNav("/cart")}
                >
                    Cart
                    {cartCount > 0 && (
                        <span className="cart-badge">{cartCount}</span>
                    )}
                </button>

                {/* PROFILE */}
                <div className="profile" ref={menuRef}>
                    <div
                        className="avatar"
                        onClick={() => setShowMenu(!showMenu)}
                        title={displayName}
                    >
                        {displayName?.charAt(0) || "U"}
                    </div>

                    {showMenu && (
                        <div className="glass-dropdown">

                            <p><b>{displayName}</b></p>

                            <button onClick={() => handleNav("/orders")}>
                                📦 My Orders
                            </button>

                            {!mobileOpen && (
                                <button onClick={() => handleNav("/cart")}>
                                    🛒 Cart
                                </button>
                            )}

                            <button onClick={() => handleNav("/wishlist")}>
                                ❤️ Wishlist
                            </button>

                            <button onClick={() => handleNav("/settings")}>
                                ⚙ Settings
                            </button>

                            <button onClick={() => handleNav("/support")}>
                                💬 Support
                            </button>

                            <button onClick={() => handleNav("/about")}>
                                ℹ️ About Us
                            </button>

                            <button onClick={() => handleNav("/partner")}>
                                🤝 Partner
                            </button>

                            <button
                                className="logout"
                                onClick={() => {
                                    localStorage.removeItem("user");
                                    localStorage.removeItem("cart");
                                    window.dispatchEvent(new Event("cart-updated"));
                                    setMobileOpen(false);
                                    setShowMenu(false);
                                    navigate("/login");
                                }}
                            >
                                🚪 Logout
                            </button>
                        </div>
                    )}

                </div>

            </div>

        </div>
    );
}